import type {ReactElement} from 'react';

import {useCountryList} from '@/context/CountryListContext';
import type {AfriexCountry, AfriexVirtualAccount} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

interface VirtualAccountInfoProps {
  data: AfriexVirtualAccount;
}

const getFieldsToDisplay = (
  data: VirtualAccountInfoProps['data'],
  countryList: AfriexCountry[],
) => {
  const flag = mapCountryCodeToFlags(countryList)[data.info.country];
  return [
    {'Bank Name': data.info.bankName},
    {'Account Name': data.info.accountName},
    {'Account Number': data.info.accountNumber},
    {'Account Phone': data.info.accountPhone},
    {'Routing Number': data.info.routingNumber},
    {'Sort Code': data.info.sortCode},
    {'Swift Code': data.info.swiftCode},
    {Processor: data.processor},
    {Status: capitalizeWords(data.status)},
    {Country: `${data.info.country} ${flag}`},
    {Currency: data.info.currency},
    {'Allowed Actions': data.capabilities.join(', ')},
    {
      'Account Created': formatDate.DDMonYYYY(data.createdAt?.toString() || ''),
    },
  ];
};

const VirtualAccountInfo = ({data}: VirtualAccountInfoProps): ReactElement => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  return (
    <div>
      {getFieldsToDisplay(data, countryList)?.map((field, idx) => {
        const key = Object.keys(field)[0];
        const value = Object.values(field)[0];

        if (!value || value?.length === 0) {
          return null;
        }

        return (
          <div
            key={idx}
            className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
          >
            <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
              {key}
            </p>
            <p className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 truncate">
              {value}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default VirtualAccountInfo;
